import type { MockSectionCustomerInput } from './mockSectionData.util';
import { mockSectionData } from './mockSectionData.util';
import { coerceBhConnectionCount } from './bhConnectionCoerce.util';
import {
  buildClinicalCareOutcomesTrend,
  CLINICAL_OUTCOMES_MINIMAL_THRESHOLD,
  type ClinicalCareOutcomesGoalRow,
} from './clinicalCareOutcomesSnapshot.util';
import type { ReportSectionFieldSources } from './reportFieldSource.util';
import { readReportFieldSource } from './reportFieldSource.util';
import { isWarehouseWhoWereReachingSnapshot } from './whoWereReachingSnapshotDetect.util';

const WAREHOUSE_FIELD_SOURCES: ReportSectionFieldSources = {
  membersInClinicalCare: 'warehouse',
  activeThisMonth: 'warehouse',
  treatmentGoalsIdentified: 'warehouse',
  symptomReductionPct3Mo: 'warehouse',
  phq9Series: 'warehouse',
  gad7Series: 'warehouse',
  months: 'warehouse',
  clinicalGoals: 'warehouse',
  insights: 'mock',
};

const MOCK_FIELD_SOURCES: ReportSectionFieldSources = {
  membersInClinicalCare: 'mock',
  activeThisMonth: 'mock',
  treatmentGoalsIdentified: 'mock',
  symptomReductionPct3Mo: 'mock',
  phq9Series: 'mock',
  gad7Series: 'mock',
  months: 'mock',
  clinicalGoals: 'mock',
  insights: 'mock',
};

const resolveCount = (
  snapshotValue: unknown,
  mockValue: unknown,
  useMockFallback: boolean,
): number => {
  const fromSnapshot = coerceBhConnectionCount(snapshotValue);
  if (fromSnapshot > 0) {
    return fromSnapshot;
  }
  return useMockFallback ? coerceBhConnectionCount(mockValue) : 0;
};

const resolvePercent = (
  snapshotValue: unknown,
  mockValue: unknown,
  useMockFallback: boolean,
): number => {
  if (snapshotValue !== null && snapshotValue !== undefined && snapshotValue !== '') {
    const fromSnapshot = coerceBhConnectionCount(snapshotValue);
    if (fromSnapshot >= 0 && typeof snapshotValue === 'number') {
      return fromSnapshot;
    }
    if (fromSnapshot > 0) {
      return fromSnapshot;
    }
  }
  return useMockFallback ? coerceBhConnectionCount(mockValue) : 0;
};

const readClinicalGoals = (rows: unknown): ClinicalCareOutcomesGoalRow[] =>
  Array.isArray(rows) ? (rows as ClinicalCareOutcomesGoalRow[]) : [];

/**
 * Normalizes clinical-care-outcomes snapshots with `fieldSources` for mock framing.
 * @param snapshot - Stored or live snapshot JSON.
 * @param customer - Customer context for mock fallback fields.
 * @param reportingPeriod - Report period label.
 * @returns Snapshot with `fieldSources` metadata for the Care Portal UI.
 */
export const normalizeClinicalCareOutcomesSnapshot = (
  snapshot: Record<string, unknown>,
  customer: MockSectionCustomerInput,
  reportingPeriod: string,
): Record<string, unknown> => {
  const mockBase = mockSectionData(
    'clinical-care-outcomes',
    customer,
    reportingPeriod,
  );
  const isWarehouse =
    isWarehouseWhoWereReachingSnapshot(snapshot) ||
    readReportFieldSource(snapshot, 'phq9Series') === 'warehouse' ||
    readReportFieldSource(snapshot, 'membersInClinicalCare') === 'warehouse';
  const useMockFallback = !isWarehouse;

  const treatmentTrend = buildClinicalCareOutcomesTrend(
    Array.isArray(snapshot.treatmentTrend)
      ? (snapshot.treatmentTrend as Array<Record<string, unknown>>)
      : [],
  );
  const trendFromSnapshot =
    treatmentTrend.length > 0
      ? treatmentTrend
      : buildClinicalCareOutcomesTrend(
          (Array.isArray(snapshot.months) ? snapshot.months : []).map(
            (month, index) => ({
              agg_months_in_bh_treatment: index + 1,
              avg_phq9_score: Array.isArray(snapshot.phq9Series)
                ? snapshot.phq9Series[index]
                : undefined,
              avg_gad7_score: Array.isArray(snapshot.gad7Series)
                ? snapshot.gad7Series[index]
                : undefined,
            }),
          ),
        );

  const months =
    trendFromSnapshot.length > 0
      ? trendFromSnapshot.map(row => `Month ${row.monthsInTreatment}`)
      : useMockFallback
        ? (mockBase.months as string[])
        : [];
  const phq9Series =
    trendFromSnapshot.length > 0
      ? trendFromSnapshot.map(row => row.phq9)
      : useMockFallback
        ? (mockBase.phq9Series as number[])
        : [];
  const gad7Series =
    trendFromSnapshot.length > 0
      ? trendFromSnapshot.map(row => row.gad7)
      : useMockFallback
        ? (mockBase.gad7Series as number[])
        : [];

  const clinicalGoals = readClinicalGoals(snapshot.clinicalGoals);
  const resolvedClinicalGoals =
    clinicalGoals.length > 0 || !useMockFallback
      ? clinicalGoals
      : readClinicalGoals(mockBase.clinicalGoals);

  return {
    ...mockBase,
    ...snapshot,
    membersInClinicalCare: resolveCount(
      snapshot.membersInClinicalCare,
      mockBase.membersInClinicalCare,
      useMockFallback,
    ),
    activeThisMonth: resolveCount(
      snapshot.activeThisMonth,
      mockBase.activeThisMonth,
      useMockFallback,
    ),
    treatmentGoalsIdentified: resolveCount(
      snapshot.treatmentGoalsIdentified,
      mockBase.treatmentGoalsIdentified,
      useMockFallback,
    ),
    symptomReductionPct3Mo: isWarehouse
      ? snapshot.symptomReductionPct3Mo ?? mockBase.symptomReductionPct3Mo
      : resolvePercent(
          snapshot.symptomReductionPct3Mo,
          mockBase.symptomReductionPct3Mo,
          useMockFallback,
        ),
    months,
    phq9Series,
    gad7Series,
    treatmentTrend: trendFromSnapshot,
    clinicalGoals: resolvedClinicalGoals,
    minimalThreshold: CLINICAL_OUTCOMES_MINIMAL_THRESHOLD,
    fieldSources: isWarehouse
      ? {
          ...MOCK_FIELD_SOURCES,
          ...WAREHOUSE_FIELD_SOURCES,
          ...(typeof snapshot.fieldSources === 'object' &&
          snapshot.fieldSources !== null
            ? (snapshot.fieldSources as ReportSectionFieldSources)
            : {}),
          insights: 'mock',
        }
      : MOCK_FIELD_SOURCES,
    snapshotSource: isWarehouse ? snapshot.snapshotSource ?? 'warehouse' : undefined,
  };
};
